"use client";

import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Popconfirm } from "antd";
import SendFIle from "../misc/SendFIle";
import { compareDate } from "../../../service/convert/date";
import { MdOutlineRecycling } from "react-icons/md";
import { getDocument, deleteDocument } from "../../../service/api/document";
import { setTitle } from "../../../store/slices/navbar";

import { useAppSelector, useAppDispatch } from "../../../store";
import { twMerge } from "tailwind-merge";

//icons
import {
  IconSend,
  IconDownload,
  IconTrash,
  IconBuilding,
  IconCalendarWeek,
  IconFileText,
} from "@tabler/icons-react";
import { ActionButton } from "../../../components/elements/form/ActionButton";
import MakeRequest from "../../../components/ui/modal/MakeRequest";

const Document = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const loading = useAppSelector(({ loading }) => loading.update);
  const document = useAppSelector(({ document }) => document.document);
  const [send, sendSet] = React.useState(false);
  const [renew, renewSet] = React.useState(false);

  React.useEffect(() => {
    if (id) {
      getDocument(dispatch, id);
    }
  }, [id, dispatch]);

  React.useEffect(() => {
    if (document?.name) {
      dispatch(setTitle(document.name));
    }
  }, [document?.name, dispatch]);

  const expired = compareDate(document?.expiring_date);

  const confirm = () => {
    deleteDocument(dispatch, document?.id, navigate);
  };

  const DetailCard = ({
    icon: Icon,
    label,
    value,
    isLink = false,
    href,
  }: {
    icon: React.ElementType;
    label: string;
    value: string;
    isLink?: boolean;
    href?: string;
  }) => (
    <div className="group p-4 rounded-xl bg-slate-50/50 hover:bg-slate-100/80 transition-all duration-200 border border-gray-100/20">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-gray-50/10 shadow-sm text-slate-500 group-hover:text-slate-700 transition-colors">
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">
            {label}
          </p>
          {isLink && href ? (
            <Link
              to={href}
              className="text-sm font-semibold text-slate-900 hover:text-blue-600 transition-colors truncate block"
            >
              {value}
            </Link>
          ) : (
            <p className="text-sm font-semibold text-slate-900 capitalize truncate">
              {value}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  const path = document?.path ? document.path.replace("undefined/", "") : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <div className="w-full">
        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-xl shadow-slate-200/50 overflow-hidden">
          <div className="grid grid-cols-1 xl:grid-cols-3">
            {/* PDF Viewer */}
            <div className="xl:col-span-2 relative bg-slate-900">
              <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900" />
              <div className="relative h-[70vh] xl:h-[85vh]">
                <iframe
                  title="PDF Document Viewer"
                  className="w-full h-full"
                  src={path}
                />
              </div>
            </div>

            {/* Document Details */}
            <div className="flex flex-col h-[70vh] xl:h-[85vh] border-l border-gray-50/20">
              <div className="flex-1 overflow-y-auto p-6 lg:p-8">
                {/* Document Info Section */}
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex justify-between items-center w-full">
                      <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                        Document Info
                      </span>

                      {document?.expired === "0" ? (
                        <span className="rounded-full bg-primary-50/20 text-sm font-medium text-primary-700 px-2 py-px">
                          Active
                        </span>
                      ) : (
                        <span className="rounded-full bg-red-50/20 text-sm font-medium text-red-700 px-2 py-px">
                          Expired
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <DetailCard
                      icon={IconFileText}
                      label="Name"
                      value={document?.name || "—"}
                    />
                    <DetailCard
                      icon={IconBuilding}
                      label="Issuing Body"
                      value={document?.issuing_body || "—"}
                    />
                    <DetailCard
                      icon={IconCalendarWeek}
                      label="Issuing Date"
                      value={document?.issuing_date || "—"}
                    />
                    <DetailCard
                      icon={IconCalendarWeek}
                      label="Expiring Date"
                      value={document?.expiring_date || "—"}
                    />

                    <ActionButton
                      title="Renew"
                      icon={MdOutlineRecycling}
                      onClick={() => renewSet(true)}
                      label="Renew document"
                      variant="primary"
                      isHidden={false}
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="p-6 lg:p-8 bg-slate-50/50 xl:border-t border-gray-50/20">
                <div className={twMerge("grid grid-cols-3 gap-3")}>
                  {expired ? (
                    <ActionButton
                      title="Renew"
                      icon={MdOutlineRecycling}
                      onClick={() => renewSet(true)}
                      label="Renew document"
                      variant="primary"
                      isHidden={true}
                    />
                  ) : (
                    <ActionButton
                      title="Send"
                      icon={IconSend}
                      onClick={() => sendSet(true)}
                      label="Send document"
                      variant="primary"
                      isHidden={true}
                    />
                  )}

                  <ActionButton
                    title="Download"
                    icon={IconDownload}
                    onClick={() => {
                      if (typeof window !== "undefined" && path) {
                        window.open(path, "_blank");
                      }
                    }}
                    label="Download document"
                    variant="secondary"
                    isHidden={true}
                  />

                  <Popconfirm
                    title="Delete Document"
                    description="Are you sure you want to delete this document? This action cannot be undone."
                    onConfirm={confirm}
                    okButtonProps={{
                      loading,
                      className:
                        "bg-red-500 hover:bg-red-600 text-white border-none",
                    }}
                    okText="Delete"
                    cancelText="Cancel"
                  >
                    <ActionButton
                      title="Delete"
                      icon={IconTrash}
                      onClick={() => {}}
                      label="Delete"
                      variant="danger"
                      isHidden={true}
                    />
                  </Popconfirm>
                </div>
              </div>
            </div>
          </div>
        </div>

        <SendFIle
          document_id={document?.id}
          visible={send}
          onSubmit={() => sendSet(false)}
        />
        {document && (
          <MakeRequest
            id={document.id}
            visible={renew}
            onFinish={() => renewSet(false)}
            type="renew"
          />
        )}
      </div>
    </div>
  );
};

export default Document;
